import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Code, 
  Key, 
  Book, 
  ArrowLeft, 
  Copy, 
  Check, 
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function ApiDocs() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState({});

  useEffect(() => {
    fetchDocs();
  }, []);

  const fetchDocs = async () => {
    try {
      console.log('[ApiDocs] Fetching from /docs endpoint...');
      const response = await api.get('/docs');
      console.log('[ApiDocs] Response received:', response.data);
      setDocs(response.data);
    } catch (error) {
      console.error('[ApiDocs] Failed to fetch API docs:', error);
      console.error('[ApiDocs] Error details:', error.response?.data);
      toast.error('Failed to load API documentation');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode({ ...copiedCode, [id]: true });
    toast.success('Code copied to clipboard');
    setTimeout(() => {
      setCopiedCode({ ...copiedCode, [id]: false });
    }, 2000);
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'bg-blue-500',
      POST: 'bg-green-500',
      PUT: 'bg-yellow-500',
      DELETE: 'bg-red-500',
      PATCH: 'bg-purple-500'
    };
    return colors[method] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0b0d10] via-[#0e1216] to-[#0b0d10] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!docs) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0b0d10] via-[#0e1216] to-[#0b0d10] flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Failed to load documentation</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0b0d10] via-[#0e1216] to-[#0b0d10]">
      {/* Header */}
      <div className="border-b border-border/40 bg-black/20 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <Book className="h-5 w-5 text-primary" />
                <h1 className="text-xl font-bold">{docs.title}</h1>
                <Badge variant="outline">{docs.version}</Badge>
              </div>
            </div>
            <Button
              onClick={() => navigate('/settings')}
              variant="outline"
              size="sm"
            >
              <Key className="h-4 w-4 mr-2" />
              Get API Key
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Introduction */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Welcome to Allioki API</CardTitle>
            <CardDescription className="text-base">{docs.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-2 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Base URL</p>
                <code className="text-sm text-blue-800 dark:text-blue-200">{docs.base_url}</code>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Getting Started */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              Getting Started
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {docs.getting_started.steps.map((step) => (
                <div key={step.step} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold">
                      {step.step}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Authentication */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Authentication
            </CardTitle>
            <CardDescription>{docs.authentication.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="curl" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="curl">cURL</TabsTrigger>
                <TabsTrigger value="python">Python</TabsTrigger>
                <TabsTrigger value="javascript">JavaScript</TabsTrigger>
                <TabsTrigger value="node">Node.js</TabsTrigger>
              </TabsList>
              {Object.entries(docs.authentication.example).map(([lang, code]) => (
                <TabsContent key={lang} value={lang} className="relative">
                  <div className="relative">
                    <pre className="p-4 bg-gray-900 rounded-lg overflow-x-auto text-sm">
                      <code className="text-gray-100">{code}</code>
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2"
                      onClick={() => copyCode(code, `auth-${lang}`)}
                    >
                      {copiedCode[`auth-${lang}`] ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>

        {/* API Endpoints */}
        <div className="space-y-6 mb-8">
          <h2 className="text-2xl font-bold">API Endpoints</h2>
          
          {docs.endpoints.map((endpoint, index) => (
            <Card key={index}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={`${getMethodColor(endpoint.method)} text-white`}>
                        {endpoint.method}
                      </Badge>
                      <code className="text-sm font-mono">{endpoint.path}</code>
                    </div>
                    <CardTitle className="text-xl">{endpoint.title}</CardTitle>
                    <CardDescription className="mt-2">{endpoint.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Parameters */}
                {endpoint.parameters && endpoint.parameters.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Parameters</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-3 font-medium">Name</th>
                            <th className="text-left p-3 font-medium">Type</th>
                            <th className="text-left p-3 font-medium">Required</th>
                            <th className="text-left p-3 font-medium">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {endpoint.parameters.map((param, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="p-3 font-mono text-xs">{param.name}</td>
                              <td className="p-3">
                                <Badge variant="outline">{param.type}</Badge>
                              </td>
                              <td className="p-3">
                                {param.required ? (
                                  <Badge variant="destructive">Required</Badge>
                                ) : (
                                  <Badge variant="secondary">Optional</Badge>
                                )}
                              </td>
                              <td className="p-3 text-muted-foreground">{param.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Request Body */}
                {endpoint.body && (
                  <div>
                    <h4 className="font-semibold mb-3">Request Body</h4>
                    <pre className="p-4 bg-gray-900 rounded-lg overflow-x-auto text-sm">
                      <code className="text-gray-100">
                        {JSON.stringify(endpoint.body, null, 2)}
                      </code>
                    </pre>
                  </div>
                )}

                {/* Response Examples */}
                {endpoint.response && (
                  <div>
                    <h4 className="font-semibold mb-3">Response</h4>
                    {endpoint.response.success && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-green-500 text-white">
                            {endpoint.response.success.code}
                          </Badge>
                          <span className="text-sm font-medium">Success</span>
                        </div>
                        <pre className="p-4 bg-gray-900 rounded-lg overflow-x-auto text-sm">
                          <code className="text-gray-100">
                            {JSON.stringify(endpoint.response.success.example, null, 2)}
                          </code>
                        </pre>
                      </div>
                    )}
                    {endpoint.response.error && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="destructive">
                            {endpoint.response.error.code}
                          </Badge>
                          <span className="text-sm font-medium">Error</span>
                        </div>
                        <pre className="p-4 bg-gray-900 rounded-lg overflow-x-auto text-sm">
                          <code className="text-gray-100">
                            {JSON.stringify(endpoint.response.error.example, null, 2)}
                          </code>
                        </pre>
                      </div>
                    )}
                  </div>
                )}

                {/* Code Examples */}
                {endpoint.code_examples && (
                  <div>
                    <h4 className="font-semibold mb-3">Code Examples</h4>
                    <Tabs defaultValue={Object.keys(endpoint.code_examples)[0]} className="w-full">
                      <TabsList className="grid w-full grid-cols-4">
                        {Object.keys(endpoint.code_examples).map((lang) => (
                          <TabsTrigger key={lang} value={lang} className="capitalize">
                            {lang}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      {Object.entries(endpoint.code_examples).map(([lang, code]) => (
                        <TabsContent key={lang} value={lang} className="relative">
                          <div className="relative">
                            <pre className="p-4 bg-gray-900 rounded-lg overflow-x-auto text-sm">
                              <code className="text-gray-100">{code}</code>
                            </pre>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="absolute top-2 right-2"
                              onClick={() => copyCode(code, `${index}-${lang}`)}
                            >
                              {copiedCode[`${index}-${lang}`] ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </TabsContent>
                      ))}
                    </Tabs>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Error Codes */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Error Codes</CardTitle>
            <CardDescription>Common HTTP status codes you may encounter</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {docs.error_codes.map((error) => (
                <div key={error.code} className="flex items-start gap-4 p-3 border rounded-lg">
                  <Badge variant="outline" className="text-base">
                    {error.code}
                  </Badge>
                  <div>
                    <h4 className="font-semibold">{error.title}</h4>
                    <p className="text-sm text-muted-foreground">{error.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Rate Limits */}
        <Card>
          <CardHeader>
            <CardTitle>Rate Limits & Billing</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{docs.rate_limits.description}</p>
            <ul className="space-y-2">
              {docs.rate_limits.details.map((detail, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <span className="text-primary">•</span>
                  <span>{detail}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
